import { useState, type FormEvent } from 'react'
import type { AskResult, LineId, VerifiedCitation } from '../../contract'
import { Ticker } from '../components/Ticker'
import { DroppedPanel } from '../components/DroppedPanel'
import { fmtTime, shortId, speakerHue } from '../format'
import { toHash, type Route } from '../router'
import type { RunState } from './ThemesView'

interface Props {
  run: RunState<AskResult>
  question: string
  setQuestion: (q: string) => void
  onAsk: (q: string) => void
  onCancel: () => void
  onOpenLine: (id: LineId) => void
  knownTranscripts: Set<string>
  route: Route
}

/** Put the popover on whichever side of its anchor has room. */
function flip(anchor: HTMLElement) {
  const r = anchor.getBoundingClientRect()
  const pop = anchor.querySelector<HTMLElement>('.pop')
  if (window.innerWidth < 640) {
    // phone: the popover spans the viewport below the badge instead of hanging off one side
    anchor.dataset.side = 'full'
    if (pop) pop.style.top = `${r.bottom + 6}px`
    return
  }
  if (pop) pop.style.top = ''
  anchor.dataset.side = window.innerWidth - r.left < Math.min(430, window.innerWidth - 24) ? 'left' : 'right'
}

const SUGGESTIONS = ['What did people say about pricing?', 'How do teams recruit participants?', 'Do people trust the AI summaries?', 'What did anyone say about video calls?']

export function AskView({ run, question, setQuestion, onAsk, onCancel, onOpenLine, knownTranscripts, route }: Props) {
  const running = run.status === 'running'
  const res = run.result
  const [openPop, setOpenPop] = useState<number | null>(null)
  const [focusClaim, setFocusClaim] = useState<string | null>(null)

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!question.trim() || running) return
    setOpenPop(null); setFocusClaim(null)
    onAsk(question.trim())
  }

  const lineHref = (c: VerifiedCitation) => toHash({ ...route, t: c.lineId, r: c.transcriptId })

  return (
    <div className="view view--ask">
      <div className="view__head">
        <div>
          <h1 className="view__title">Ask the corpus</h1>
          <p className="view__lede">Every sentence of the answer links to the line it came from. A sentence without a source is shown as dropped, never as text.</p>
        </div>
      </div>

      <form className="askform" onSubmit={submit}>
        <input
          className="askform__input"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="What did people say about pricing?"
          aria-label="Question"
          data-testid="ask-input"
          autoComplete="off"
        />
        {running ? (
          <button type="button" className="btn" onClick={onCancel} data-testid="ask-cancel">Stop</button>
        ) : (
          <button className="btn btn--primary" disabled={!question.trim()} data-testid="ask-submit">Ask</button>
        )}
      </form>
      {run.status === 'idle' && (
        <div className="suggest" data-testid="suggestions">
          {SUGGESTIONS.map((s) => <button key={s} className="chipbtn" onClick={() => { setQuestion(s); onAsk(s) }}>{s}</button>)}
        </div>
      )}

      {(running || run.events.length > 0) && (
        <Ticker events={run.events} status={running ? 'Searching the corpus…' : `Done · ${res?.model ?? ''}`} running={running} />
      )}
      {run.status === 'error' && <p className="error" role="alert">Ask failed: {run.error}</p>}

      {running && run.text && (
        <div className="draft card" data-testid="draft">
          <div className="draft__label">Unverified draft, streaming. Nothing here counts until each sentence has a receipt.</div>
          <p className="draft__text">{run.text}<span className="caret" /></p>
        </div>
      )}

      {res && res.nothingFound && (
        <div className="nobody card" data-testid="nothing-found">
          <h2>Nobody said that.</h2>
          <p>No line in the corpus supports an answer to <q>{res.question}</q>. Rather than make something up, the answer is empty. The searches the model ran are listed above so you can judge whether it looked properly.</p>
        </div>
      )}

      {res && !res.nothingFound && (
        <div className="answer-layout">
          <article className="answer card" data-testid="answer" aria-label="Answer">
            <div className="answer__q">Q · {res.question}</div>
            <p className="answer__body">
              {res.sentences.map((s, i) => {
                if (s.citations.length === 0) {
                  return (
                    <span key={i} className="sent">
                      <button
                        type="button"
                        className="sentence sentence--dropped"
                        data-testid="sentence-dropped"
                        data-cited="false"
                        title="No verbatim source. Shown as dropped, see the Dropped panel."
                        onClick={() => setFocusClaim(s.text)}
                      >
                        <span className="sentence__text">{s.text}</span>
                        <span className="sentence__tag">no receipt</span>
                      </button>{' '}
                    </span>
                  )
                }
                const first = s.citations[0]
                const isOpen = openPop === i
                return (
                  <span key={i} className={`sent${isOpen ? ' sent--open' : ''}`}>
                    <a
                      onMouseEnter={(e) => { const a = e.currentTarget.parentElement?.querySelector<HTMLElement>('.sent__anchor'); a && flip(a) }}
                      className="sentence sentence--cited"
                      data-testid="sentence-cited"
                      data-cited="true"
                      data-line-id={first.lineId}
                      href={lineHref(first)}
                      onClick={(e) => { e.preventDefault(); onOpenLine(first.lineId) }}
                    >
                      {s.text}
                    </a>
                    <span className="sent__anchor" onMouseEnter={(e) => flip(e.currentTarget)}>
                    <button
                      type="button"
                      className="sent__n"
                      aria-label={`${s.citations.length} source${s.citations.length === 1 ? '' : 's'}`}
                      aria-expanded={isOpen}
                      onClick={(e) => { flip(e.currentTarget.parentElement!); setOpenPop(isOpen ? null : i) }}
                    >{s.citations.length}</button>
                    <span className="pop" role="tooltip" data-testid="pop">
                      {s.citations.map((c, j) => (
                        <a key={j} className="pop__cite" href={lineHref(c)} onClick={(e) => { e.preventDefault(); setOpenPop(null); onOpenLine(c.lineId) }}>
                          <span className="pop__quote">“{c.quote}”</span>
                          <span className="pop__meta">
                            {c.speaker && <span className="quote__speaker" style={{ ['--hue' as string]: speakerHue(c.speaker) }}>{c.speaker}</span>}
                            {c.t !== null && <span className="mono">{fmtTime(c.t)}</span>}
                            <span className="quote__src">{shortId(c.transcriptId)}</span>
                          </span>
                        </a>
                      ))}
                    </span>
                    </span>{' '}
                  </span>
                )
              })}
            </p>
            <div className="answer__foot muted">
              {res.sentences.filter((s) => s.citations.length).length} of {res.sentences.length} sentences sourced · model <span className={`chip${res.model === 'stub' ? ' chip--warn' : ''}`}>{res.model}</span>
            </div>
          </article>
          <DroppedPanel dropped={res.dropped} ran onOpenLine={onOpenLine} knownTranscripts={knownTranscripts} focusClaim={focusClaim} />
        </div>
      )}
    </div>
  )
}
